import { useState, useEffect } from 'react';
import { getDatabase, ref, onValue } from 'firebase/database';
import { auth } from '../firebase';

interface UserFavorite {
  name: string;
  type: 'school' | 'program';
  schoolName?: string; // For programs, this is the school that offers the program
  sortOrder: number;
}

export const useUserFavorites = () => {
  const [favorites, setFavorites] = useState<UserFavorite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setFavorites([]);
      setLoading(false);
      return;
    }

    const db = getDatabase();
    const userFavoritesRef = ref(db, `user/${currentUser.uid}/favorites`);
    
    // Set up real-time listener for favorites
    const unsubscribe = onValue(userFavoritesRef, (snapshot) => {
      try {
        setLoading(true);
        setError(null);

        if (snapshot.exists()) {
          const favoritesData = snapshot.val();
          const favoritesList: UserFavorite[] = [];

          for (const itemName in favoritesData) {
            const itemData = favoritesData[itemName];
            if (itemData.FavoritedType === 'school' || itemData.FavoritedType === 'program') {
              favoritesList.push({
                name: itemName,
                type: itemData.FavoritedType,
                schoolName: itemData.FavoritedType === 'program' ? itemData.FavoritedLocation?.split(',')[0]?.trim() : undefined,
                sortOrder: itemData.FavoritedSortOrder || 0
              });
            }
          }

          // Sort by sort order
          favoritesList.sort((a, b) => a.sortOrder - b.sortOrder);
          setFavorites(favoritesList);
        } else {
          setFavorites([]);
        }
      } catch (err: any) {
        console.error('Error fetching user favorites:', err);
        setError('Failed to fetch favorites');
        setFavorites([]);
      } finally {
        setLoading(false);
      }
    }, (error) => {
      console.error('Error in favorites listener:', error);
      setError('Failed to fetch favorites');
      setFavorites([]);
      setLoading(false);
    });

    // Cleanup function
    return () => unsubscribe();
  }, []);

  const refreshFavorites = () => {
    // The real-time listener will automatically update when data changes
    // This function is kept for backward compatibility
  };

  return {
    favorites,
    loading,
    error,
    refreshFavorites
  };
};
